/**
 * Cancellation for a render that runs on the main thread.
 *
 * Rendering is synchronous CPU work there, so while a long document draws, a
 * Cancel cannot even be DISPATCHED — the click waits in the queue for the very
 * render it was meant to stop. Handing the event loop a turn every so many
 * drawn lines lets that click through, and lets the signal cut the work short.
 */

/** 250 is below a pause anyone notices and gives the longest period the fetch
 *  will return (5000 activity rows) some twenty ways out. */
const YIELD_EVERY_LINES = 250;

export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException("Protocol render cancelled", "AbortError");
}

/** Called once per drawn row; checks the signal every row, yields periodically. */
export type Pacer = () => Promise<void>;

export function pacer(signal?: AbortSignal): Pacer {
  let drawn = 0;
  return async () => {
    // Every row, not only every yield: a document shorter than the yield
    // interval draws in a single task and would otherwise never notice a cancel
    // that landed while the fonts and the logo were still being fetched.
    throwIfAborted(signal);
    drawn += 1;
    if (drawn < YIELD_EVERY_LINES) return;
    drawn = 0;
    // A macrotask, not a microtask: a queued click runs between tasks, so
    // awaiting an already-resolved promise would yield to nothing.
    await new Promise((resolve) => window.setTimeout(resolve, 0));
    throwIfAborted(signal);
  };
}
